import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { FileText, Plus } from "lucide-react";

const CUR = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const fmt = (minor, cur) => `${CUR[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  DRAFT: "bg-[#EAEAEA] text-black",
  READY: "bg-[#FFE7CD] text-[#8A4A00]",
  SENT: "bg-[#FFE7CD] text-[#8A4A00]",
  PARTIALLY_PAID: "bg-[#FFF3D0] text-[#7A5A00]",
  PAID: "bg-[#D6F5D6] text-[#0E5A17]",
  OVERDUE: "bg-[#FFD3D3] text-[#8A0F0F]",
  CANCELLED: "bg-[#EAEAEA] text-[#8A8A8A]",
};

/** Reusable invoices summary + list panel.
 *  Filter: pass `businessId` to scope to one customer; pass `projectId` to
 *  scope to one project. When both are passed, projectId narrows further.
 */
export default function HQInvoicesPanel({ businessId, projectId, projectValueMinor, title = "Invoices & Payments", createLabel = "Create invoice" }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    const params = {};
    if (businessId) params.business_id = businessId;
    hqApi.get("/api/hq/invoices", { params })
      .then((r) => {
        if (!alive) return;
        let list = r.data.items || [];
        if (projectId) list = list.filter((i) => i.project_id === projectId);
        setItems(list);
      })
      .catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [businessId, projectId]);

  const totals = useMemo(() => {
    const t = {}; // { currency: { invoiced, paid, outstanding, overdue } }
    (items || []).forEach((inv) => {
      if (inv.status === "CANCELLED") return;
      const c = inv.currency;
      t[c] = t[c] || { invoiced: 0, paid: 0, outstanding: 0, overdue: 0 };
      t[c].invoiced += Number(inv.total_amount_minor || 0);
      t[c].paid += Number(inv.amount_paid_minor || 0);
      t[c].outstanding += Number(inv.amount_outstanding_minor || 0);
      if (inv.status === "OVERDUE") t[c].overdue += Number(inv.amount_outstanding_minor || 0);
    });
    return t;
  }, [items]);

  const currencies = Object.keys(totals);
  const createHref = (() => {
    const params = new URLSearchParams();
    if (businessId) params.set("business_id", businessId);
    if (projectId) params.set("project_id", projectId);
    const qs = params.toString();
    return `/hq/invoices/new${qs ? `?${qs}` : ""}`;
  })();

  const testId = projectId ? `hq-project-invoices-panel` : `hq-business-invoices-panel`;

  return (
    <div className="border-2 border-black rounded-2xl bg-white overflow-hidden" data-testid={testId}>
      <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-[#EAEAEA] flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" />
          <h3 className="font-black tracking-[-0.01em] text-lg">{title}</h3>
        </div>
        <Link to={createHref} data-testid={`${testId}-create`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black text-white text-[10px] font-bold tracking-[0.16em] uppercase">
          <Plus className="w-3 h-3" /> {createLabel}
        </Link>
      </div>

      <div className="px-5 py-4">
        {projectValueMinor != null && (
          <div className="mb-3">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Project value</div>
            <div className="mt-1 font-black text-2xl">{fmt(projectValueMinor, currencies[0] || "GBP")}</div>
          </div>
        )}
        {items == null ? (
          <div className="text-black/50 text-sm">Loading…</div>
        ) : currencies.length === 0 ? (
          <div className="text-black/50 text-sm" data-testid={`${testId}-empty`}>No invoices yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {currencies.map((c) => (
              <React.Fragment key={c}>
                <SummaryTile label="Total invoiced" value={fmt(totals[c].invoiced, c)} testId={`${testId}-invoiced-${c}`} />
                <SummaryTile label="Total paid" tone="pos" value={fmt(totals[c].paid, c)} testId={`${testId}-paid-${c}`} />
                <SummaryTile label="Outstanding" tone="warn" value={fmt(totals[c].outstanding, c)} testId={`${testId}-outstanding-${c}`} />
                <SummaryTile label="Overdue" tone="err" value={fmt(totals[c].overdue, c)} testId={`${testId}-overdue-${c}`} />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {items && items.length > 0 && (
        <div className="border-t border-[#EAEAEA]">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FFFDF6]">
              <tr className="text-left [&_th]:px-4 [&_th]:py-2.5 [&_th]:font-bold [&_th]:text-[10px] [&_th]:tracking-[0.18em] [&_th]:uppercase [&_th]:text-black/60">
                <th>Invoice</th><th>Description</th><th>Total</th><th>Paid</th><th>Outstanding</th><th>Status</th><th>Due</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`${testId}-row-${i.invoice_number}`}>
                  <td className="px-4 py-2.5 font-black">
                    <Link to={`/hq/invoices/${i.id}`} className="hover:underline">{i.invoice_number}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-black/80">{i.description || "—"}</td>
                  <td className="px-4 py-2.5 font-bold">{fmt(i.total_amount_minor, i.currency)}</td>
                  <td className="px-4 py-2.5">{fmt(i.amount_paid_minor, i.currency)}</td>
                  <td className="px-4 py-2.5">{fmt(i.amount_outstanding_minor, i.currency)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-black tracking-[0.16em] uppercase ${STATUS_STYLES[i.status] || "bg-[#EAEAEA]"}`}>
                      {(i.status || "").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-black/60">{i.due_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone, testId }) {
  const cls = { pos: "text-[#0E5A17]", warn: "text-[#8A4A00]", err: "text-[#8A0F0F]" }[tone] || "text-black";
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-black/50">{label}</div>
      <div className={`mt-1 font-black text-xl ${cls}`} data-testid={testId}>{value}</div>
    </div>
  );
}
